import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import {
  Activity,
  ArrowRight,
  BarChart3,
  BrainCircuit,
  Calendar,
  GraduationCap,
} from 'lucide-react';

const RESEARCH_SECTIONS = [
  {
    title: 'Markets',
    description: 'High-frequency market discovery and options research.',
    icon: BarChart3,
    accent: 'from-primary/15 via-primary/5 to-transparent',
    items: [
      {
        label: 'Market Overview',
        path: '/market',
        description: 'Browse instruments, movers, and quick lists.',
      },
      {
        label: 'Screener',
        path: '/screener',
        description: 'Run technical and fundamental filters across markets.',
      },
      {
        label: 'Set Up Alerts',
        path: '/alerts',
        description: 'Create alerts for symbols and technical conditions you want to monitor.',
      },
      {
        label: 'Options',
        path: '/fundamentals/options',
        description: 'Inspect options-related market context.',
      },
    ],
  },
  {
    title: 'Macro',
    description: 'Scheduled events and broad market drivers.',
    icon: Calendar,
    accent: 'from-emerald-500/15 via-emerald-500/5 to-transparent',
    items: [
      {
        label: 'Economic Calendar',
        path: '/fundamentals/economic-calendar',
        description: 'Track upcoming releases and macro events.',
      },
      {
        label: 'Economic Indicators',
        path: '/fundamentals/economic-indicators',
        description: 'Review indicator series and macro snapshots.',
      },
      {
        label: 'COT Report',
        path: '/fundamentals/cot-report',
        description: 'Monitor positioning in futures markets.',
      },
    ],
  },
  {
    title: 'Intelligence',
    description: 'Narrative, ownership, and relationship-based research.',
    icon: BrainCircuit,
    accent: 'from-amber-500/15 via-amber-500/5 to-transparent',
    items: [
      {
        label: 'News',
        path: '/fundamentals/news',
        description: 'Scan current headlines and market-moving stories.',
      },
      {
        label: 'Insider Activity',
        path: '/fundamentals/insider-trading',
        description: 'Review insider transactions and ownership signals.',
      },
      {
        label: 'Knowledge Graph',
        path: '/fundamentals/knowledge-graph',
        description: 'Explore connected entities and relationships.',
      },
    ],
  },
  {
    title: 'Analysis',
    description: 'Deeper workflows for structured review and planning.',
    icon: Activity,
    accent: 'from-sky-500/15 via-sky-500/5 to-transparent',
    items: [
      {
        label: 'Historical Analysis',
        path: '/analysis',
        description: 'Run and review longer-form analysis jobs.',
      },
      {
        label: 'Win-Rate Calculator',
        path: '/win-rate',
        description: 'Model expectancy and win-rate tradeoffs quickly.',
      },
      {
        label: 'Tutorial',
        path: '/tutorial',
        description: 'Learn AI systems, experts, agents, trading concepts, and automation workflows.',
      },
    ],
  },
];

const HERO_ACTIONS = [
  {
    label: 'Screen for setups',
    description: 'Use Screener when you already know the conditions you want to filter for.',
    path: '/screener',
    icon: BarChart3,
  },
  {
    label: 'Set up alerts',
    description: 'Use Alerts when you want the platform to notify you after a setup appears.',
    path: '/alerts',
    icon: Activity,
  },
  {
    label: 'Check macro catalysts',
    description: 'Use Economic Calendar when timing, volatility, or event risk matters most.',
    path: '/fundamentals/economic-calendar',
    icon: Calendar,
  },
  {
    label: 'Read market context',
    description: 'Use News when you need narrative and current context before drilling into symbols.',
    path: '/fundamentals/news',
    icon: BrainCircuit,
  },
  {
    label: 'Run deeper analysis',
    description: 'Use Historical Analysis when an idea needs a more structured investigation.',
    path: '/analysis',
    icon: Activity,
  },
  {
    label: 'Learn AI automation',
    description: 'Use Tutorial for the AI systems, agents, experts, and trading automation course.',
    path: '/tutorial',
    icon: GraduationCap,
  },
];

function ResearchSection({ section, onNavigate }) {
  const Icon = section.icon;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className={`bg-gradient-to-br ${section.accent} p-5 border-b border-borderDefault`}>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-backgroundSurface/90 p-2 border border-borderDefault">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-textPrimary">{section.title}</h2>
              <p className="text-sm text-textSecondary mt-1">{section.description}</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {section.items.map((item) => (
            <button
              key={item.path}
              onClick={() => onNavigate(item.path)}
              className="group w-full rounded-xl border border-borderDefault bg-backgroundSurface p-4 text-left transition hover:border-primary/40 hover:bg-primary/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-textPrimary group-hover:text-primary transition-colors">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm text-textSecondary">{item.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-textSecondary group-hover:text-primary transition-colors mt-0.5" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ResearchHubPage() {
  const navigate = useNavigate();

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-7xl space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-borderDefault bg-gradient-to-br from-primary/10 via-backgroundSurface to-backgroundSurface p-6 sm:p-8">
        <div className="relative z-10 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary/80">Research Hub</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-textPrimary">
              Start with the question you need to answer
            </h1>
            <p className="mt-3 text-sm sm:text-base text-textSecondary leading-relaxed">
              Use this page to choose the right research workflow quickly: screen for candidates, check event risk,
              build narrative context, or run deeper analysis. The underlying pages are unchanged, but this hub now
              helps you decide where to begin.
            </p>
          </div>

          <div className="rounded-2xl border border-borderDefault bg-backgroundSurface/80 p-4 sm:p-5 xl:max-w-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-textSecondary">How To Use It</p>
            <div className="mt-3 space-y-2 text-sm text-textSecondary">
              <p>If you already know your filters, start with Screener.</p>
              <p>If you want signals to come back to you, set up an alert.</p>
              <p>If timing and catalysts matter, start with Macro tools.</p>
              <p>If conviction is still weak, start with News or deeper Analysis.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 relative z-10">
          {HERO_ACTIONS.map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="group rounded-xl border border-borderDefault bg-backgroundSurface/80 p-4 text-left transition hover:border-primary/40 hover:bg-backgroundSurface"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="rounded-lg border border-borderDefault bg-backgroundInput p-2">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-textSecondary">
                    Quick Start
                  </span>
                </div>
                <p className="mt-4 text-sm font-semibold text-textPrimary group-hover:text-primary transition-colors">
                  {action.label}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-textSecondary">
                  {action.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {RESEARCH_SECTIONS.map((section) => (
          <ResearchSection key={section.title} section={section} onNavigate={navigate} />
        ))}
      </div>
    </div>
  );
}
